import React from 'react';
import { Plugins } from '@leverege/plugin';
import { ValueCache } from '@leverege/value-cache';
import Attributes from '../../attribute/Attributes.js';
import Formatters from '../../attribute/formatter/Formatters.js';
import I18nResolvers from '../../attribute/i18nResolver/I18nResolvers.js';
import { LabelText, ValueText } from "./ColumnText.js";
import Util from './Util.js';
import InlineEditor from "../../attribute/editor/InlineEditor.js";
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY = {};
const USAGE = 'table';

/**
 * This is a generic column renderer for an Attribute. It will render the label
 * and the first value in the returned formatter array. To use, either derive
 * from this renderer or install it directly in the Plugins against the column name.
 *
 *  Plugins.add( 'ColumnRenderer', {
 *    type : Util.getColumnTypeForType( 'MyType' ),
 *    renderer : AttributeColumnRenderer } )
 */
export default class AttributeColumnRenderer {
  constructor(column) {
    this.column = column;
    this.name = column.attrName;
    this.objectType = column.objectType;
    this.cellRendererCache = new ValueCache();
    this.renderCell = this.renderCell.bind(this);
  }

  /**
   * Returns a component to be rendered as the value
   */
  getHeaderComponent = tableContext => {
    const {
      header: labelOpts
    } = this.column;
    const attribute = Attributes.getAttribute(this.name, this.objectType);
    const label = this.column?.header?.label || attribute.displayName || '';
    const ls = Util.getHeaderStyle(labelOpts);
    const variant = Util.getVariant(labelOpts, undefined, {
      columnModel: this.column,
      usage: USAGE
    });
    return /*#__PURE__*/_jsx(LabelText, {
      variant: variant,
      style: ls,
      children: label
    });
  };
  renderCell(data, props, context) {
    const {
      cell: valueOpts
    } = this.column;
    const {
      value
    } = data;
    const original = data?.row?.original || data?.original;
    const vs = Util.getStyle(valueOpts, original /* , cxt */);
    const variant = Util.getVariant(valueOpts, original, {
      context,
      value,
      columnModel: this.column,
      usage: USAGE
    });
    const resolvedValue = this.formatAndI18nResolveValue(value);
    return /*#__PURE__*/_jsx(ValueText, {
      variant: variant,
      style: vs,
      children: resolvedValue
    });
  }
  formatAndI18nResolveValue = value => {
    if (value == null) {
      return this.column?.cell?.emptyValue;
    }
    const formattedValue = Formatters.format(value, this.column.formatter);
    return I18nResolvers.resolve(formattedValue, this.column);
  };
  getData = rowObj => {
    const attr = Attributes.getAttribute(this.name, rowObj.type);
    return attr?.get?.(rowObj.data);
  };
  filterRenderersByType = renderer => {
    return renderer.objectType === this.column.objectType;
  };
  getContext(object, cxt) {
    return {
      ...cxt,
      renderer: this,
      target: [object],
      usage: USAGE
    };
  }
  getCellRenderer = (renderers, context) => {
    /* eslint-disable react/function-component-definition,react/destructuring-assignment,react/no-this-in-sfc */
    return (data, props) => {
      const original = data?.row?.original || data?.original;
      const {
        editable
      } = context || {};
      const cxt = this.getContext(original, context);
      if (this.column.formatter) {
        cxt.formatter = this.column.formatter;
      }
      const cellRenderer = renderers.find(rnd => {
        return this.column.attrName === rnd.attrName && (rnd.handles == null || rnd.handles && rnd.handles(cxt));
      });
      const cr = cellRenderer ? cellRenderer.renderer(data, props, cxt) : this.renderCell(data, props, cxt);

      // The Inline Editor will render the cr if there is not a replacement
      // editor for it.

      const {
        cell: valueOpts
      } = this.column;

      // calculate the style and variant or the editor to use
      const vs = Util.getStyle(valueOpts, original /* , cxt */);
      const variant = Util.getVariant(valueOpts, original, {
        context,
        data,
        columnModel: this.column,
        usage: USAGE
      });
      return /*#__PURE__*/_jsx(InlineEditor, {
        usage: "table",
        editable: editable,
        attrName: this.name,
        object: original,
        variant: variant,
        style: vs,
        context: cxt,
        children: cr
      }, `cell-${data.column.id}-${data.row.id}`);
    };
    /* eslint-enable react/function-component-definition,react/destructuring-assignment,react/no-this-in-sfc */
  };
  getColumnInfo(context = EMPTY) {
    const {
      width,
      minWidth,
      maxWidth
    } = this.column;
    const renderers = Plugins.createView('AttributeCellRenderer', {
      name: this.column.objectType,
      filter: this.filterRenderersByType
    }).get();
    const rtn = {
      id: this.column.id,
      Header: this.getHeaderComponent,
      Cell: this.cellRendererCache.calc(this.getCellRenderer, renderers, context),
      accessor: this.getData
    };
    if (width != null) {
      rtn.width = width;
    }
    if (minWidth != null) {
      rtn.minWidth = minWidth;
    }
    if (maxWidth != null) {
      rtn.maxWidth = maxWidth;
    }
    return rtn;
  }
}